import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN, stickerButton } from "../../theme";
import { COUNTING_ENGINE_THEME } from "../../../../data/countingLadderCatalog";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  CARD_FACE, CountingPanel, CountingPiece, CountingRoot, DRAG_BUTTON, EDGE_IDLE, PanelChip, POP_SPRING, ShakeWrap, SupplyShelf, darkenHex, hexAlpha,
  useCompleteOnce, useCountingDrag, useWrongFlash,
} from "../CountingPiece";

const TARGET = { hundreds: 6, tens: 4, ones: 5 };
const WORD = "six hundred forty-five";
const PLACES = [
  { id: "hundreds", label: "Hundreds", asset: "cube", unit: "hundred", value: 100 },
  { id: "tens", label: "Tens", asset: "rod", unit: "ten", value: 10 },
  { id: "ones", label: "Ones", asset: "bead", unit: "one", value: 1 },
];
const MAX_PER_PLACE = 9;
/** The three supply stickers: chunky, but capped so the columns keep most of the arena. */
const SUPPLY_SIZE = "max(56px, min(calc(var(--tile) * 1.5), calc(var(--arena-h, 640px) * 0.2)))";

/**
 * Build the number-word card with place-value blocks: drag a hundred
 * block, ten rod or one bead into its column (or tap a column to add one
 * of its unit), then check the number you built.
 */
const PlaceValueBuilder = ({ speak, sfx, onComplete, onWrong, reduceMotion = false }) => {
  const accent = COUNTING_ENGINE_THEME.sequenceRepair.accent;
  const [counts, setCounts] = useState({ hundreds: 0, tens: 0, ones: 0 });
  const [selected, setSelected] = useState(null); // place id of the lifted supply piece
  const [solved, setSolved] = useState(false);
  const [burst, setBurst] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const { flash, isWrong } = useWrongFlash();
  const built = counts.hundreds * 100 + counts.tens * 10 + counts.ones;

  const add = (placeId) => {
    if (solved) return false;
    if (counts[placeId] >= MAX_PER_PLACE) { flash(placeId); return false; }
    setCounts((c) => ({ ...c, [placeId]: c[placeId] + 1 }));
    setSelected(null);
    sfx?.("snap");
    return true;
  };

  const remove = (placeId) => {
    if (solved || counts[placeId] === 0) return;
    setCounts((c) => ({ ...c, [placeId]: c[placeId] - 1 }));
    sfx?.("pop", { rate: 0.9 });
  };

  const dropPiece = (pieceId, placeId) => {
    if (pieceId !== placeId) {
      flash(placeId);
      onWrong?.();
      return false;
    }
    return add(placeId);
  };

  const check = () => {
    if (solved) return;
    if (counts.hundreds === TARGET.hundreds && counts.tens === TARGET.tens && counts.ones === TARGET.ones) {
      setSolved(true);
      sfx?.("correct");
      speak?.("six hundred forty-five", { interrupt: true });
      setBurst((b) => b + 1);
      complete();
      return;
    }
    flash("readout");
    onWrong?.();
  };

  const { dragProps, registerTarget, hoverId, guardTap, GhostLayer } = useCountingDrag({
    reduceMotion,
    sfx,
    onDrop: ({ payload, targetId, targetData }) => (targetId ? dropPiece(payload, targetData.place) : false),
  });

  const ghostFor = (asset) => () => <CountingPiece asset={asset} variant="bare" size="100%" />;

  return (
    <CountingRoot>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "calc(var(--tile) * 0.14)", justifyContent: "center", alignItems: "center" }}>
        <PanelChip text={WORD} accent={accent} style={{ position: "static", fontSize: "clamp(17px, calc(var(--tile) * 0.24), 24px)", padding: "8px 20px" }} />
        <ShakeWrap shake={isWrong("readout")} reduceMotion={reduceMotion} style={{ display: "inline-flex", gap: 4 }} aria-label={`Built number ${built}`}>
          {PLACES.map((p) => (
            <span
              key={p.id}
              style={{
                width: "calc(var(--tile) * 0.74)", minWidth: 44, height: "calc(var(--tile) * 0.8)", minHeight: 50, borderRadius: "calc(var(--tile) * 0.16)", display: "grid", placeItems: "center",
                background: solved ? `linear-gradient(180deg, #fff, ${hexAlpha(LEARN.color.mint, 0.24)})` : CARD_FACE,
                border: `3px solid ${solved ? LEARN.color.mint : hexAlpha(accent, 0.5)}`,
                boxShadow: `inset 0 2px 0 rgba(255,255,255,0.9), 0 4px 0 ${solved ? LEARN.color.mintDeep : EDGE_IDLE}`,
                fontWeight: 700, fontSize: "clamp(26px, calc(var(--tile) * 0.36), 34px)", color: LEARN.color.ink, fontVariantNumeric: "tabular-nums",
              }}
            >
              {counts[p.id]}
            </span>
          ))}
        </ShakeWrap>
      </div>

      <CountingPanel grow tint={accent} style={{ padding: "calc(var(--tile) * 0.12)", paddingBottom: "calc(var(--tile) * 0.12)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "clamp(6px, 1.5vmin, 12px)", width: "100%", height: "100%", minHeight: "calc(var(--tile) * 2.4)" }}>
          {PLACES.map((p) => {
            const hover = hoverId === p.id;
            const active = selected != null;
            const full = counts[p.id] >= MAX_PER_PLACE;
            return (
              <ShakeWrap key={p.id} shake={isWrong(p.id)} reduceMotion={reduceMotion} style={{ display: "flex", minWidth: 0, minHeight: 0 }}>
                <div
                  ref={(el) => registerTarget(p.id, el, { place: p.id })}
                  role="button"
                  tabIndex={0}
                  aria-label={`${p.label} column, ${counts[p.id]}`}
                  onClick={guardTap(() => {
                    if (solved) return;
                    if (selected != null) { dropPiece(selected, p.id); return; }
                    add(p.id);
                  })}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (selected != null) dropPiece(selected, p.id); else add(p.id); } }}
                  className="counting-btn counting-target"
                  aria-disabled={solved ? "true" : undefined}
                  style={{
                    flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", alignItems: "center", borderRadius: LEARN.radius.md, "--target-accent": accent,
                    background: hover
                      ? `linear-gradient(180deg, #fff, ${hexAlpha(accent, 0.3)})`
                      : `linear-gradient(180deg, ${LEARN.color.white} 0%, ${hexAlpha(LEARN.color.blueSoft, 0.4)} 70%, ${hexAlpha(accent, 0.1)} 100%)`,
                    border: `3px solid ${hover || (active && selected === p.id) ? accent : hexAlpha(accent, 0.3)}`,
                    boxShadow: `inset 0 2px 0 rgba(255,255,255,0.9), 0 calc(var(--tile) * 0.07) 0 ${hover ? hexAlpha(accent, 0.8) : EDGE_IDLE}, 0 calc(var(--tile) * 0.12) calc(var(--tile) * 0.2) ${hexAlpha(LEARN.color.ink, 0.1)}`,
                    cursor: solved ? "default" : "pointer", transition: "background 140ms, border-color 140ms, box-shadow 140ms, transform 140ms",
                    transform: hover ? "scale(1.03)" : "scale(1)", padding: 0, touchAction: "manipulation", position: "relative", isolation: "isolate",
                  }}
                >
                  {/* the bin's name plate */}
                  <span
                    style={{
                      alignSelf: "stretch", textAlign: "center", padding: "calc(var(--tile) * 0.08) 6px", fontWeight: 700, fontSize: "clamp(14px, calc(var(--tile) * 0.2), 18px)",
                      color: darkenHex(accent, 0.25), letterSpacing: "0.03em", textTransform: "uppercase", background: hexAlpha(accent, 0.12), borderBottom: `2px dashed ${hexAlpha(accent, 0.35)}`,
                      borderRadius: `${LEARN.radius.md - 3}px ${LEARN.radius.md - 3}px 0 0`,
                    }}
                  >
                    {p.label}
                  </span>
                  <div style={{ flex: 1, minHeight: 0, width: "100%", display: "flex", flexWrap: "wrap-reverse", alignContent: "flex-start", justifyContent: "center", gap: 2, overflow: "auto", padding: "4px 4px calc(var(--tile) * 0.2)", boxSizing: "border-box", position: "relative", zIndex: 1 }}>
                    <AnimatePresence>
                      {Array.from({ length: counts[p.id] }, (_, i) => (
                        <motion.button
                          key={i}
                          type="button"
                          className="counting-btn"
                          aria-label={`Remove a ${p.unit}`}
                          onClick={(e) => { e.stopPropagation(); remove(p.id); }}
                          initial={reduceMotion ? false : { scale: 0.3, y: -20, opacity: 0 }}
                          animate={{ scale: 1, y: 0, opacity: 1 }}
                          exit={reduceMotion ? { opacity: 0 } : { scale: 0.3, opacity: 0 }}
                          transition={reduceMotion ? { duration: 0 } : POP_SPRING}
                          disabled={solved}
                          style={{ ...DRAG_BUTTON, padding: 0, cursor: solved ? "default" : "pointer" }}
                        >
                          <CountingPiece asset={p.asset} variant="bare" size="max(44px, calc(var(--tile) * 0.62))" reduceMotion={reduceMotion} />
                        </motion.button>
                      ))}
                    </AnimatePresence>
                  </div>
                  {/* the bin's front rim: the blocks stand behind it */}
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute", left: 0, right: 0, bottom: 0, height: "calc(var(--tile) * 0.16)", zIndex: 2, pointerEvents: "none",
                      borderRadius: `0 0 ${LEARN.radius.md - 3}px ${LEARN.radius.md - 3}px`, borderTop: `2px solid ${hexAlpha(accent, full ? 0.7 : 0.4)}`,
                      background: `linear-gradient(180deg, ${hexAlpha(accent, full ? 0.34 : 0.2)}, ${hexAlpha(accent, full ? 0.46 : 0.32)})`, transition: "background 200ms, border-color 200ms",
                    }}
                  />
                </div>
              </ShakeWrap>
            );
          })}
        </div>
        {burst > 0 && <ConfettiBurst key={`burst-${burst}`} count={30} originY={40} reduceMotion={reduceMotion} />}
      </CountingPanel>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "calc(var(--tile) * 0.12)", justifyContent: "center", alignItems: "stretch" }}>
        <SupplyShelf label="Block supply" style={{ width: "auto", flex: "1 1 auto", padding: "calc(var(--tile) * 0.1) calc(var(--tile) * 0.24) calc(var(--tile) * 0.24)", gap: "calc(var(--tile) * 0.2)" }}>
          {PLACES.map((p) => {
            const dp = dragProps(p.id, ghostFor(p.asset));
            const isSel = selected === p.id;
            return (
              <motion.div key={p.id} animate={{ y: isSel ? -6 : 0 }} style={{ display: "inline-flex" }}>
                <button
                  type="button"
                  {...dp}
                  aria-label={`${p.label === "Hundreds" ? "Hundred block" : p.label === "Tens" ? "Ten rod" : "One bead"}`}
                  className="counting-btn"
                  disabled={solved}
                  onClick={guardTap(() => { if (solved) return; sfx?.("pop"); setSelected((s) => (s === p.id ? null : p.id)); })}
                  style={{ ...DRAG_BUTTON, ...dp.style, padding: 0, cursor: solved ? "default" : "grab" }}
                >
                  <CountingPiece asset={p.asset} variant="sticker" size={SUPPLY_SIZE} floats={!isSel && !solved} lifted={isSel} ring={isSel ? accent : undefined} reduceMotion={reduceMotion} />
                </button>
              </motion.div>
            );
          })}
        </SupplyShelf>
        <button type="button" className="counting-btn" onClick={check} disabled={solved} style={{ ...stickerButton({ bg: solved ? LEARN.color.mint : accent, deep: solved ? LEARN.color.mintDeep : darkenHex(accent), size: "md" }), cursor: solved ? "default" : "pointer", alignSelf: "center" }}>
          Check my number
        </button>
      </div>
      <GhostLayer />
    </CountingRoot>
  );
};

export default PlaceValueBuilder;
